/**
 * Task 12 (`.superpowers/sdd/2026-09-08-volunteering-and-admin-centres/task-12-brief.md`).
 *
 * CONTROLLER RULING: this predicate lives in its own file, not inside
 * `src/constants/sideBarRoutes.js` (plain JS, no types) and not hand-copied
 * into the sidebar renderer. `sideBarRoutes.js` keeps only the declarative
 * `requiresVolunteerConnection: true` flag on the volunteering entry; the
 * sidebar renderer imports and calls this function.
 *
 * Show the Volunteering entry when:
 *   - volunteering is ALL this person can do (he holds manage_volunteering and
 *     none of the app's own permissions) -- it is his only page, and it must
 *     survive him leaving his last Chabad House; or
 *   - he is an account holder who has actually offered help somewhere.
 *
 * An account holder who has never volunteered sees nothing, which is the point:
 * every existing anash holds the permission and none of them asked for a new
 * sidebar item. He reaches the page the first time from the /dashboard card's
 * "Add your Chabad House" row (Phase 4), not from the sidebar.
 *
 * `read_households` stands in for "holds any of the app's own permissions" --
 * every account holder (admin/anash/etc.) has it; a pure volunteer never does.
 *
 * NOTE ON THE PARAMETER TYPE: the real signed-in-user object
 * (`IUser` in `src/types/reducers/authReducer.ts`) carries `permissions` as
 * `{ general: string[]; scoped: { permissions: string[]; ... } }`, not a flat
 * `string[]` -- so this function deliberately takes a narrow, pre-flattened
 * shape rather than `IUser` itself. Callers (the sidebar renderer) are
 * responsible for flattening `permissions.general` + `permissions.scoped.permissions`
 * before calling in, the same way `utils/permissions.ts`'s `hasPermission` does.
 */
export const showsVolunteering = (user: {
  permissions?: string[];
  has_volunteer_connection?: boolean;
  /** V1.2: pressed "I want to volunteer" in My settings (cleared again when all volunteering is stopped). */
  volunteering_opted_in?: boolean;
}): boolean => {
  const perms = user.permissions ?? [];
  if (!perms.includes("manage_volunteering")) return false;
  if (!perms.includes("read_households")) return true; // volunteering is all he has
  return Boolean(user.has_volunteer_connection || user.volunteering_opted_in);
};

/**
 * Whole-branch review fix (MINOR 6), 2026-09-08.
 *
 * `showsVolunteering` above deliberately takes a pre-flattened `string[]`, so
 * every caller had to hand-copy the flattening -- which SideBar.tsx and
 * SideBarMobile/index.tsx each did, in duplicate, defeating the point of
 * extracting the predicate in the first place. These three wrappers take the
 * real signed-in-user object instead and do the flattening once.
 *
 * Kept dependency-free on purpose: importing `utils/permissions.ts` here would
 * close a cycle (permissions.ts -> backend/sagas/onboardingRedirect.ts ->
 * this file). The flattening below is the same "general OR scoped.permissions"
 * rule `hasPermission` applies, spelled out rather than imported.
 */
type UserLike = {
  permissions?: {
    general?: string[];
    scoped?: { permissions?: string[] };
  };
  has_volunteer_connection?: boolean;
  volunteering_opted_in?: boolean;
} | null | undefined;

/** general + scoped.permissions, the same two lists `hasPermission` consults. */
export const flattenPermissions = (user: UserLike): string[] => [
  ...(user?.permissions?.general ?? []),
  ...(user?.permissions?.scoped?.permissions ?? []),
];

/**
 * `showsVolunteering` for a real signed-in-user object.
 *
 * NOTE: this is NOT the volunteer-only classifier -- that is `isVolunteerOnly`
 * in utils/permissions.ts, and the two answer different questions. This one
 * asks "should the Volunteering entry be drawn in the sidebar", and is
 * deliberately the broader of the two: someone who volunteers at a Chabad House
 * but is a `limited` member of a workspace still wants the entry, even though
 * his entire app is emphatically not /volunteering.
 */
export const showsVolunteeringFor = (user: UserLike): boolean =>
  showsVolunteering({
    permissions: flattenPermissions(user),
    has_volunteer_connection: user?.has_volunteer_connection,
    volunteering_opted_in: user?.volunteering_opted_in,
  });
